import { HasToBePicGuard } from 'app/core/guards/HasToBePicGuard';
import AgencyProfileLayout from 'app/main/agency/profile/AgencyProfileLayout';
import Error404Page from 'app/main/errors/Error404Page';
import DataLoader from 'app/ui_v2/components/data-loader/DataLoader';
import { cloneDeep } from 'lodash-es';
import { lazy, Suspense } from 'react';
import { Navigate, RouteObject } from 'react-router-dom';

import AgencyProfileResolver from '../protected-routes/AgencyProfileResolver';
import { getAgencyPerformanceRoutes } from './agencyPerformanceRoutes';

const AgencyProfileSections = lazy(
  () => import('../../../../main/agency/profile/profile-sections/AgencyProfileSections')
);

const AgencyAvailabilitySections = lazy(
  () => import('../../../../main/agency/profile/availability-sections/AgencyAvailabilitySections')
);

const AgencyDetailsSections = lazy(
  () => import('../../../../main/agency/profile/details-sections/AgencyDetailsSections')
);

const AgencyDisciplinesSections = lazy(
  () => import('../../../../main/agency/profile/qualifications-sections/AgencyDisciplinesSections')
);

const AgencyBankAccountSection = lazy(
  () => import('../../../../main/agency/profile/bank-account-section/AgencyBankAccountSection')
);

const StaffRoster = lazy(() => import('../../../../main/agency/profile/users-sections/staff-roster/StaffRoster'));

const AddMember = lazy(
  () => import('../../../../main/agency/profile/users-sections/staff-roster/sections/add-member/AddMember')
);
const EditMember = lazy(
  () => import('../../../../main/agency/profile/users-sections/staff-roster/sections/edit-member/EditMember')
);

const NewQaReport = lazy(
  () => import('../../../../main/agency/profile/performance-sections/new-qa-report/NewQaReport')
);
const QaReportView = lazy(
  () => import('../../../../main/agency/profile/performance-sections/qa-report-view/QaReportView')
);

export const agencyProfileRoutes: RouteObject[] = [
  {
    path: '',
    element: (
      <AgencyProfileResolver>
        <AgencyProfileLayout />
      </AgencyProfileResolver>
    ),
    children: [
      {
        path: '',
        element: <Navigate replace to="main" />,
      },
      {
        path: 'main',
        element: (
          <Suspense fallback={<DataLoader />}>
            <AgencyProfileSections />
          </Suspense>
        ),
      },
      {
        path: 'availability',
        element: (
          <Suspense fallback={<DataLoader />}>
            <AgencyAvailabilitySections />
          </Suspense>
        ),
      },
      {
        path: 'details',
        element: (
          <Suspense fallback={<DataLoader />}>
            <AgencyDetailsSections />
          </Suspense>
        ),
      },
      {
        path: 'disciplines',
        element: (
          <Suspense fallback={<DataLoader />}>
            <AgencyDisciplinesSections />
          </Suspense>
        ),
      },
      {
        path: 'metrics',
        children: getAgencyPerformanceRoutes([]),
      },
      {
        path: 'payout-setup',
        element: (
          <Suspense fallback={<DataLoader />}>
            <AgencyBankAccountSection />
          </Suspense>
        ),
      },
      {
        path: 'qualifications',
        element: <Navigate to="../disciplines" />,
      },
      {
        path: 'users',
        element: (
          <Suspense fallback={<DataLoader />}>
            <StaffRoster />
          </Suspense>
        ),
      },
      {
        path: 'users/add-member',
        element: (
          <Suspense fallback={<DataLoader />}>
            <AddMember />
          </Suspense>
        ),
      },
      {
        path: 'users/:invitationId',
        element: (
          <Suspense fallback={<DataLoader />}>
            <EditMember />
          </Suspense>
        ),
      },
      {
        path: '*',
        element: <Error404Page />,
      },
      {
        path: ':any/*',
        element: <Error404Page />,
      },
    ],
  },
  {
    path: 'metrics/qa-report/new',
    element: (
      <AgencyProfileResolver>
        <HasToBePicGuard>
          <NewQaReport />
        </HasToBePicGuard>
      </AgencyProfileResolver>
    ),
  },
  {
    path: 'metrics/qa-report/:reportId',
    element: (
      <AgencyProfileResolver>
        <QaReportView view="agency" />
      </AgencyProfileResolver>
    ),
  },
];

export const getAgencyProfileRoutes = (excludes: string[]): RouteObject[] => {
  const routes = cloneDeep(agencyProfileRoutes);
  const result =
    routes[0].children?.filter((route: RouteObject) => {
      const path = route.path || '';
      return !excludes.some((exclude) => path.includes(exclude));
    }) ?? [];

  routes[0].children = result;
  return routes;
};
